import React, { useState } from 'react';
import clsx from 'clsx';
import { createStyles, lighten, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import Tooltip from '@material-ui/core/Tooltip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import FilterListIcon from '@material-ui/icons/FilterList';
import { FaList } from 'react-icons/fa';
import { ImScissors } from 'react-icons/im';
import $ from 'jquery';

import { ArrayDerivacoes, Derivacao } from '../../interfaces';
import { Button } from '../../../../themes/components/Button';
import { converterStringValorEmFloat, formatarStringQuantidade, mascaraValorEmReal, mascaraValorEmDolar } from '../../../../util/fieldMasks';
import { useLocalStorage } from '../../../../contexts/LocalStorage';
import { useModals } from '../../../../contexts/Modals';
import { validarQuantidadeCorte, validarToleranciaPreco } from '../../../../util/rulesSale';
import { Background } from 'victory';
import { FieldDropdown } from '../../../../themes/components/FieldDropdown';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (parseInt(String(b[orderBy])) < parseInt(String(a[orderBy]))) {
    return -1;
  }
  if (parseInt(String(b[orderBy])) > parseInt(String(a[orderBy]))) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Derivacao;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  { id: 'codclc', numeric: false, disablePadding: true, label: 'Agrupamento' },
  { id: 'codctn', numeric: false, disablePadding: false, label: 'Container' },
  { id: 'codemp', numeric: false, disablePadding: false, label: 'Empresa' },
  { id: 'codder', numeric: false, disablePadding: false, label: 'Derivação' },
  { id: 'codpro', numeric: false, disablePadding: false, label: 'Produto' },
  { id: 'salime', numeric: true, disablePadding: false, label: 'Saldo Imediato' },
  { id: 'salpro', numeric: true, disablePadding: false, label: 'Saldo Programado' },
  { id: 'valor', numeric: true, disablePadding: false, label: 'Valor Unitário (R$)' },
  { id: 'quantidade', numeric: true, disablePadding: false, label: 'Quantidade' },
  { id: 'quantidadecorte', numeric: true, disablePadding: false, label: 'Corte' },
  { id: 'valortotal', numeric: true, disablePadding: false, label: 'Total (R$)' },
];

const headCellsDollar: HeadCell[] = [
  { id: 'codclc', numeric: false, disablePadding: true, label: 'Agrupamento' },
  { id: 'codctn', numeric: false, disablePadding: false, label: 'Container' },
  { id: 'codemp', numeric: false, disablePadding: false, label: 'Empresa' },
  { id: 'codder', numeric: false, disablePadding: false, label: 'Derivação' },
  { id: 'codpro', numeric: false, disablePadding: false, label: 'Produto' },
  { id: 'salime', numeric: true, disablePadding: false, label: 'Saldo Imediato' },
  { id: 'salpro', numeric: true, disablePadding: false, label: 'Saldo Programado' },
  { id: 'valor', numeric: true, disablePadding: false, label: 'Valor Unitário (R$)' },
  { id: 'precoVendaUSD', numeric: true, disablePadding: false, label: 'Valor Unitário (U$)' },
  { id: 'quantidade', numeric: true, disablePadding: false, label: 'Quantidade' },
  { id: 'quantidadecorte', numeric: true, disablePadding: false, label: 'Corte' },
  { id: 'valortotal', numeric: true, disablePadding: false, label: 'Total (R$)' },
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Derivacao) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  allowSelectitem: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const { getFromSessionStorage } = useLocalStorage();

  const createSortHandler = (property: keyof Derivacao) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {props.allowSelectitem ?
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'select all desserts' }}
            />
          </TableCell>
          :
          <></>
        }
        {(getFromSessionStorage("pedDol") === "S" ? headCellsDollar : headCells).map((headCell) => (

          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ whiteSpace: 'nowrap' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1)
    },
    highlight:
      theme.palette.type === 'light'
        ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
        : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
    title: {
      display: 'flex',
      alignItems: 'center',
      flex: '1 1 100%',
      flexWrap: 'nowrap'
    },
  }),
);

interface EnhancedTableToolbarProps {
  numSelected: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    title: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      flex: '1 1 100%',
      flexWrap: 'nowrap'
    },
  }),
);

interface tableProps {
  allowSelectitem: boolean,
  setItensTabela: React.Dispatch<React.SetStateAction<any>>,
  itensTabela: ArrayDerivacoes,
  setSelected: React.Dispatch<React.SetStateAction<string[]>>,
  selected: string[],
  valueX: number,
  setValueX: React.Dispatch<React.SetStateAction<number>>,
  idItemErro: string,
  setIdItemErro: React.Dispatch<React.SetStateAction<string>>
}

export function TabelaAddItemNovoPedido(props: tableProps) {
  const { mostrarInfoOK } = useModals();
  const { saveInSessionStorage, getFromSessionStorage } = useLocalStorage();
  const classes = useStyles();
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Derivacao>('codder');
  const [page, setPage] = useState(1);
  const [dense, setDense] = useState(true);
  const [tempPrice, setTempPrice] = useState("");
  const [tempPriceDol, setTempPriceDol] = useState("");
  const [valueX, setValueX] = useState(0);
  const rowsPerPage = 1000;
  const pedidoEmDolar = getFromSessionStorage("pedDol") !== null ? String(getFromSessionStorage("pedDol")) : "N";
  const [estoque, setEstoque] = useState("Todos");
  const [iEstoque, setIEstoque] = useState("0");
  const optEstoque = [
    { "cod": "0", "nome": "Todos" },
    { "cod": "2", "nome": "GA" },
    { "cod": "4", "nome": "AB" }
  ];
  const cotDolar = ((getFromSessionStorage("cotDol") ? getFromSessionStorage("cotDol")?.toString() : "0"));

  const EnhancedTableToolbar = (propsChild: EnhancedTableToolbarProps) => {
    const classes = useToolbarStyles();
    const [valorAplicar, setValorAplicar] = useState<string>(getFromSessionStorage("valoraplicar") !== null ? String(getFromSessionStorage("valoraplicar")) : "");
    const [qtdAplicar, setQtdAplicar] = useState<string>(getFromSessionStorage("qtdaplicar") !== null ? String(getFromSessionStorage("qtdaplicar")) : "");
    const { numSelected } = propsChild;

    function handleValorAplicar(event: any) {
      setValorAplicar(event.target.value);
      saveInSessionStorage("valoraplicar", event.target.value);
    }

    function handleQtdAplicar(event: any) {
      setQtdAplicar(event.target.value);
      saveInSessionStorage("qtdaplicar", formatarStringQuantidade(event.target.value));
    }

    function getCodEstoque(text: string) {
      let sit = "";
      optEstoque.forEach((opt) => {
        if (opt.nome === text) {
          sit = opt.cod;
        }
      })
      return sit;
    }

    function getEstoqueList() {
      let temp: string[] = []
      optEstoque.forEach((opt) => {
        temp.push(opt.nome)
      })
      return temp;
    }

    return (
      <Toolbar
        className={clsx(classes.root, {
          [classes.highlight]: numSelected > 0,
        })}
      >
        <>
          {props.itensTabela.length > 0 &&
            <div className="area-functions-table-additens">
              <div className={classes.title} id="tableTitle">
                <Typography style={{ marginRight: '0.5em', whiteSpace: 'nowrap' }}>Empresa</Typography>
                <div className="filtro-estoque">
                  <div className={classes.title} id="" style={{ height: '2em', border: 'none', borderRadius: '5px', fontSize: '0.9em', marginRight: '1em' }}>
                    <FieldDropdown
                      value={estoque}
                      id="seleciona-combobox-estoque"
                      options={getEstoqueList()}
                      label={(option: string) => option}
                      onChange={(event, newInputValue) => {
                        setEstoque(newInputValue);
                        setIEstoque(getCodEstoque(newInputValue));
                      }}
                    />
                  </div>
                </div>

                <Typography style={{ marginRight: '0.5em', whiteSpace: 'nowrap' }}>Valor unitário</Typography>
                <input id="valor-unitario-add" type="text" style={{ height: '2em', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.9em', marginRight: '1em' }} onChange={(event) => handleValorAplicar(event)} value={pedidoEmDolar === "S" ? mascaraValorEmDolar(valorAplicar) : mascaraValorEmReal(valorAplicar)} />
                <Typography style={{ marginRight: '0.5em' }}>Quantidade</Typography>
                <input id="quantidade-add" type="text" style={{ height: '2em', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.9em' }} onChange={handleQtdAplicar} value={qtdAplicar.replace(".", ",")} />
              </div>
              <button className="btn-options-table-item color-default" onClick={() => handleAplicar()}>
                <Typography>Aplicar</Typography><FaList size={18} />
              </button>
              <button className="btn-options-table-item color-default" onClick={() => handleDistribuir()}>
                <Typography>Distribuir</Typography><FilterListIcon />
              </button>
            </div>
          }
        </>
      </Toolbar>
    );
  };


  function handleAplicar() {
    let valorAdd = (document.getElementById("valor-unitario-add") as HTMLInputElement)?.value;
    let qtdAdd = (document.getElementById("quantidade-add") as HTMLInputElement)?.value;
    if (valorAdd !== "" || qtdAdd !== "") {
      props.selected.forEach((element) => {
        if (valorAdd !== "") {
          if (pedidoEmDolar === "S" && element.substring(element.length - 3, element.length) !== "999") {
            handlePrecoDolar({ target: { value: valorAdd } }, element);
          } else {
            handlePreco({ target: { value: valorAdd } }, element);
          }
        }
        if (qtdAdd !== "") handleQuantidade({ target: { value: qtdAdd } }, element);
      })
    }
  }

  function handleDistribuir() {
    let valorAdd = (document.getElementById("valor-unitario-add") as HTMLInputElement)?.value;
    let qtdAdd = (document.getElementById("quantidade-add") as HTMLInputElement)?.value;
    if (qtdAdd !== "") {

      //Array de entrada
      let produtos: { prod: string, qtd: any }[] = [];
      props.selected.forEach((element) => {
        for (let index = 0; index < props.itensTabela.length; index++) {
          const item = props.itensTabela[index];
          if (element === item.codder + item.codpro + item.codctn) {
            //console.log(parseFloat(item.salime) + parseFloat(item.salpro));
            produtos.push({ prod: element, qtd: parseFloat(item.salime) + parseFloat(item.salpro) })
          }
        }
      })

      //Quantidade a ser distribuida / entrada do usuário
      let retirar = Math.floor(parseInt(qtdAdd));

      // somar o saldo de todos os produtos
      let saldoprod = 0;
      let soma: { prod: string, qtd: any; }[] = [];
      let prod1 = "";

      let saldo = retirar;
      let vmaior = 0;
      let imaior = 0;

      for (let i = 0; i < produtos.length; i++) {

        saldoprod += produtos[i].qtd

        //copiar o array de entranda
        prod1 = produtos[i].prod;
        soma.push({ prod: prod1, qtd: 0 });

        //pega o produto com o maior saldo
        if (produtos[i].qtd > vmaior) {
          vmaior = produtos[i].qtd;
          imaior = i;

        }
      };

      //Calculo de porcentagem para distribuir entre os produtos
      let parte = Math.floor(retirar / produtos.length);

      //Verifica se o saldo distribuído bate com o solicitado

      if (retirar >= saldoprod) {
        for (let cont = 0; cont < produtos.length; cont++) {
          soma[cont].qtd = produtos[cont].qtd;
          saldo -= soma[cont].qtd;
        };
      } else {

        for (let i = 0; i < produtos.length; i++) {

          if (((produtos[i].qtd > 1) || (produtos[i].qtd === 1)) && (Math.floor(produtos[i].qtd)) <= parte) {
            soma[i].qtd = produtos[i].qtd;
            saldo -= produtos[i].qtd;
          }

          if (((produtos[i].qtd > 1) || (produtos[i].qtd === 1)) && (Math.floor(produtos[i].qtd)) > parte) {
            soma[i].qtd = parte;
            saldo -= parte;
          }

          if ((i === (produtos.length - 1) && (saldo > 0)) && ((vmaior - soma[imaior].qtd) > saldo)) {
            soma[imaior].qtd += saldo;
            saldo = (saldo - saldo);
          }

          let d = ((soma[imaior].qtd) - (produtos[i].qtd));
          if ((i === (produtos.length - 1) && (saldo > 0)) && ((vmaior - soma[imaior].qtd) <= saldo)) {
            soma[imaior].qtd = produtos[imaior].qtd;
            saldo -= d;
          }

        };
      };

      let entregue = 0;
      if ((saldo > 0)) {

        for (let i = 0; i < produtos.length; i++) {
          entregue += soma[i].qtd;
        };

        for (let k = 0; k < produtos.length; k++) {
          let def = produtos[k].qtd - soma[k].qtd

          if ((entregue < retirar) && (def > 0) && (def > (retirar - entregue))) {
            soma[k].qtd += (retirar - entregue);
          }

          if ((entregue < retirar) && (def > 0) && (def < (retirar - entregue))) {
            soma[k].qtd += def;
          }
        };

      }

      //Aplicando valores
      props.selected.forEach((element, index) => {
        if (valorAdd !== "") {
          if (pedidoEmDolar === "S" && element.substring(element.length - 3, element.length) !== "999") {
            handlePrecoDolar({ target: { value: valorAdd } }, element);
          } else {
            handlePreco({ target: { value: valorAdd } }, element);
          }
        }
        if (qtdAdd !== "") handleQuantidade({ target: { value: soma[index].qtd } }, element);
      })
    }
  }

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Derivacao) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };


  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    let newSelecteds = [];
    if (event.target.checked) {
      for (let index = 0; index < props.itensTabela.length; index++) {
        let elemento = props.itensTabela[index].codder + props.itensTabela[index].codpro + props.itensTabela[index].codctn
        newSelecteds.push(elemento);
      }
      props.setSelected(newSelecteds);
      props.setValueX(props.valueX + 1);
    } else {
      props.setSelected([]);
      props.setValueX(props.valueX + 1);
    }
  };

  const handleClick = (event: string, elemento: string) => {
    const selectedIndex = props.selected.indexOf(elemento);
    let newSelected: string[] = [];
    let temp = props.selected;

    if (event === "adicionar") {
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(props.selected, elemento);
      } else {
        newSelected = temp;
      }
    } else if (event === "remover") {
      if (selectedIndex >= 0) {
        temp.splice(selectedIndex, 1);
      }
      newSelected = temp;
    } else if (selectedIndex === -1) {
      newSelected = newSelected.concat(props.selected, elemento);
    } else {
      temp.splice(selectedIndex, 1);
      newSelected = temp;
    }
    props.setSelected(newSelected);
    setValueX(valueX + 1);
    props.setValueX(props.valueX + 1);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const isSelected = (elemento: string) => props.selected.indexOf(elemento) !== -1;

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, props.itensTabela.length - (page - 1) * rowsPerPage);

  function handlePreco(event: any, id: string) {
    let tempTable = props.itensTabela;
    let total;
    let index = -1;
    for (let cont = 0; index < tempTable.length; cont++) {
      const element = tempTable[cont];
      if (element.codder + element.codpro + element.codctn === id) {
        index = cont;
        break;
      }
    }
    if (index >= 0) {
      if (event.target.value === "") {
        tempTable[index].precoVenda = "";
        tempTable[index].valortotal = 0
      } else {
        tempTable[index].precoVenda = converterStringValorEmFloat(event.target.value);
        total = parseFloat(props.itensTabela[index].quantidade) * parseFloat(props.itensTabela[index].precoVenda)
        tempTable[index].valortotal = total
        if (props.idItemErro !== "" && tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn === props.idItemErro) {
          props.setIdItemErro("");
        }
      }
      props.setItensTabela(tempTable);
      props.setValueX(props.valueX + 1);
    } else {
      console.error("Falha ao setar o preço no item");
    }
  }

  function handlePrecoDolar(event: any, id: string) {
    let tempTable = props.itensTabela;
    let total;
    let cotacaoDolar: string | number | null = getFromSessionStorage("cotDol");
    let cotacaoDolarConv = 0
    if (cotacaoDolar !== null) {
      cotacaoDolarConv = parseFloat(cotacaoDolar);
    }
    let index = -1;
    for (let cont = 0; index < tempTable.length; cont++) {
      const element = tempTable[cont];
      if (element.codder + element.codpro + element.codctn === id) {
        index = cont;
        break;
      }
    }
    if (index >= 0) {
      if (event.target.value === "") {
        tempTable[index].precoVendaUSD = "";
        tempTable[index].valortotal = 0;
      } else {
        tempTable[index].precoVendaUSD = converterStringValorEmFloat(event.target.value);
        if (cotacaoDolarConv > 0) {
          let valorDolarxCotacao = parseFloat(props.itensTabela[index].precoVendaUSD) * cotacaoDolarConv;
          total = parseFloat(props.itensTabela[index].quantidade) * valorDolarxCotacao;
          tempTable[index].precoVenda = (valorDolarxCotacao).toFixed(2);
        } else {
          total = parseFloat(props.itensTabela[index].quantidade) * parseFloat(props.itensTabela[index].precoVendaUSD)
        }
        tempTable[index].valortotal = total;
        if (props.idItemErro !== "" && tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn === props.idItemErro) {
          props.setIdItemErro("");
        }
      }
      //console.log("handle preco dolar")
      //console.log(tempTable[index].precoVenda)
      props.setItensTabela(tempTable);
      props.setValueX(props.valueX + 1);
    } else {
      console.error("Falha ao setar o preço no item");
    }
  }

  function handleClickPreco(id: string) {
    let tempTable = props.itensTabela;
    let index = -1;
    for (let cont = 0; index < tempTable.length; cont++) {
      const element = tempTable[cont];
      if (element.codder + element.codpro + element.codctn === id) {
        index = cont;
        break;
      }
    }
    if (index >= 0) {
      setTempPrice(tempTable[index].precoVenda ? tempTable[index].precoVenda : tempTable[index].pretab);
      tempTable[index].precoVenda = "";
      tempTable[index].valortotal = 0;
      props.setItensTabela(tempTable);
      props.setValueX(props.valueX + 1);
    } else {
      console.error("Falha ao clicar e limpar o preço");
    }
  }


  function onBlurFieldPrice(id: string) {
    let tempTable = props.itensTabela;
    let index = -1;
    for (let cont = 0; index < tempTable.length; cont++) {
      const element = tempTable[cont];
      if (element.codder + element.codpro + element.codctn === id) {
        index = cont;
        break;
      }
    }
    if (index >= 0) {
      if (!tempTable[index].precoVenda) {
        if (tempPrice) {
          tempTable[index].precoVenda = tempPrice;
          tempTable[index].valortotal = parseFloat(tempTable[index].quantidade) * parseFloat(tempPrice);
        }

      }
      if (!tempTable[index].precoVendaUSD) {
        if (tempPriceDol) {
          tempTable[index].precoVenda = tempPrice;
          tempTable[index].precoVendaUSD = tempPriceDol;
          tempTable[index].valortotal = parseFloat(tempTable[index].quantidade) * parseFloat(tempPrice);
        }
      }
      props.setItensTabela(tempTable);
      props.setValueX(props.valueX + 1);
    } else {
      console.error("Falha ao clicar e limpar o preço");
    }
  }

  function handleClickPrecoDolar(id: string) {
    let tempTable = props.itensTabela;
    let index = -1;
    for (let cont = 0; index < tempTable.length; cont++) {
      const element = tempTable[cont];
      if (element.codder + element.codpro + element.codctn === id) {
        index = cont;
        break;
      }
    }

    if (index >= 0) {
      if (tempTable[index].codctn != "999") {
        const precoBr = (tempTable[index].precoVenda ? tempTable[index].precoVenda : tempTable[index].pretab).replace(",",".");
        const precoDolar = (parseFloat(precoBr?precoBr:"0") / parseFloat(cotDolar ? cotDolar : "0")).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setTempPriceDol(precoDolar.replace(",", "."));
        setTempPrice((parseFloat(precoDolar.replace(",", ".")) * parseFloat(cotDolar ? cotDolar : "0")).toString());
        tempTable[index].precoVendaUSD = "";
        tempTable[index].valortotal = 0;
        props.setItensTabela(tempTable);
        props.setValueX(props.valueX + 1);
      }
    } else {
      console.error("Falha ao clicar e limpar o preço");
    }
  }

  function handleCorte(event: any, index: number) {
    let tempTable = props.itensTabela;
    if (event.target.value !== "") {
      if (parseInt(event.target.value) >= 0) {
        tempTable[index].quantidadecorte = parseInt(event.target.value)
        if (validarQuantidadeCorte(parseInt(tempTable[index].qtdpcs), parseInt(event.target.value))) {
          if (props.idItemErro !== "" && tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn === props.idItemErro) {
            props.setIdItemErro("");
          }
        } else {
          props.setIdItemErro(tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn);
          mostrarInfoOK("A quantidade de corte não pode ser maior que o tamanho da peça", "Tamanho da peça: " + tempTable[index].qtdpcs);
        }
      } else {
        props.setIdItemErro(tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn);
        mostrarInfoOK("O campo de corte não pode ter valor negativo");
        tempTable[index].quantidadecorte = 0;
      }
    } else {
      tempTable[index].quantidadecorte = "vazio"
      if (props.idItemErro !== "" && tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn === props.idItemErro) {
        props.setIdItemErro("");
      }
    }
    props.setItensTabela(tempTable);
    props.setValueX(props.valueX + 1);
  }

  function handleQuantidade(event: any, id: string) {
    let tempTable = props.itensTabela;
    let total;
    let index = -1;
    for (let cont = 0; index < tempTable.length; cont++) {
      const element = tempTable[cont];
      if (element.codder + element.codpro + element.codctn === id) {
        index = cont;
        break;
      }
    }
    if (index >= 0) {
      if (event.target.value === "") {
        tempTable[index].quantidade = "";
        total = 0
        tempTable[index].valortotal = 0
        handleClick("remover", tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn);
      } else {
        let value = formatarStringQuantidade(event.target.value);
       //console.log(tempTable[index]);
        tempTable[index].quantidade = value;
        if (tempTable[index].precoVenda !== undefined) {
          if (tempTable[index].precoVenda !== "0.00" && tempTable[index].precoVenda !== "" && tempTable[index].precoVenda !== "00") {

            if (pedidoEmDolar === "S" && tempTable[index].codctn != "999") {
              const precoBr = tempTable[index].precoVenda.replace(",",".");
              const precoDolar = (parseFloat(precoBr?precoBr:"0") / parseFloat(cotDolar ? cotDolar : "0")).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
              let real = parseFloat(precoDolar.replace(",", ".")) * parseFloat(cotDolar ? cotDolar : "0");
              total = (parseFloat(tempTable[index].quantidade) * real);

            } else {
              total = parseFloat(tempTable[index].quantidade) * parseFloat(tempTable[index].precoVenda.replace(",","."));
            }


          } else {
            tempTable[index].precoVenda = parseFloat(tempTable[index].pretab).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });

            if (pedidoEmDolar === "S" && tempTable[index].codctn != "999") {
              const precoBr = tempTable[index].precoVenda.replace(",",".");
              const precoDolar = (parseFloat(precoBr?precoBr:"0") / parseFloat(cotDolar ? cotDolar : "0")).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
              let real = parseFloat(precoDolar.replace(",", ".")) * parseFloat(cotDolar ? cotDolar : "0");
              total = (parseFloat(tempTable[index].quantidade) * real);
            } else {
              total = parseFloat(tempTable[index].quantidade) * parseFloat(tempTable[index].pretab.replace(",","."))
            }
          }
        } else {
          tempTable[index].precoVenda = parseFloat(tempTable[index].pretab).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
          if (pedidoEmDolar === "S" && tempTable[index].codctn != "999") {
            const precoBr = (tempTable[index].precoVenda).replace(",",".");
            const precoDolar = (parseFloat(precoBr?precoBr:"0") / parseFloat(cotDolar ? cotDolar : "0")).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
            let real = parseFloat(precoDolar.replace(",", ".")) * parseFloat(cotDolar ? cotDolar : "0");
            total = (parseFloat(tempTable[index].quantidade) * real);
          } else {
            total = parseFloat(tempTable[index].quantidade) * parseFloat(tempTable[index].pretab.replace(",","."))
          }
        }
        tempTable[index].valortotal = total
        handleClick("adicionar", tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn);

        //validando quantidade x saldo
        if (parseFloat(tempTable[index].salpro) + parseFloat(tempTable[index].salime) < parseFloat(value)) {
          props.setIdItemErro(tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn);
        }
      }
      if (props.idItemErro !== "" && tempTable[index].codder + tempTable[index].codpro + tempTable[index].codctn === props.idItemErro) {
        props.setIdItemErro("");
      }
      props.setItensTabela(tempTable);
      props.setValueX(props.valueX + 1);
    } else {
      console.error("Falha ao editar quantidade do item");
    }
  }

  const TextOnlyTooltip = withStyles({
    tooltip: {
      marginTop: '-10px',
      backgroundColor: '#00438b'
    }
  })(Tooltip);

  function getFocusCorte(id: string) {
    console.log("Pegando o foco " + id);
    $(function () {
      (document.getElementById(id) as HTMLInputElement)?.focus({ preventScroll: false });
    })
  }

  return (
    <div className={"additen-modal-root"}>
      {props.itensTabela.length > 0 ?
        <Paper className={"additen-modal-paper"}>
          {props.allowSelectitem &&
            <div style={{ display: 'flex', justifyContent: 'space-between', marginLeft: '1em' }}>
              <TextOnlyTooltip title="Densidade da Tabela" placement="top">
                <FormControlLabel
                  control={<Switch checked={dense} onChange={handleChangeDense} />}
                  label=""
                />
              </TextOnlyTooltip>
              {props.selected.length > 0 ? (
                <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
                  {props.selected.length} {props.selected.length > 1 ? "itens selecionados" : "item selecionado"}
                </Typography>
              ) : (
                <Typography className={classes.title} variant="h6" id="tableTitle" component="div">

                </Typography>
              )}
              <EnhancedTableToolbar numSelected={props.selected.length} />
            </div>
          }
          <TableContainer>
            <Table
              className={"additen-modal-table"}
              aria-labelledby="tableTitle"
              size={dense ? 'small' : 'medium'}
              aria-label="enhanced table"
            >
              <EnhancedTableHead
                classes={classes}
                numSelected={props.selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={props.itensTabela.length}
                allowSelectitem={props.allowSelectitem}
              />
              <TableBody>
                {stableSort(props.itensTabela, getComparator(order, orderBy))
                  .slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.codder + row.codpro + row.codctn);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    let totalResult;
                    let atualQtd;
                    let precoUnit;
                    let precoUnitDolar;
                    if (row.precoVenda === undefined) {
                      precoUnit = parseFloat(row.pretab).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    } else if (row.precoVenda === "") {
                      precoUnit = ""
                    } else {
                      precoUnit = parseFloat(row.precoVenda.replace(",", ".")).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    }

                    //console.log(row);

                    if (pedidoEmDolar === "S" && row.codctn != "999") {
                      if (row.precoVendaUSD === undefined) {
                        if (precoUnit) {
                          let valorEmDolar = (parseFloat(precoUnit?.replace(",", ".")) / parseFloat(cotDolar ? cotDolar : "0")).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          let valorEmReal = (parseFloat(valorEmDolar.replace(",", ".")) * parseFloat(cotDolar ? cotDolar : "0")).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
                          precoUnitDolar = valorEmDolar ? valorEmDolar : "";
                          precoUnit = valorEmReal;
                        }

                      } else if (row.precoVendaUSD === "") {
                        precoUnitDolar = ""
                      } else {
                        precoUnitDolar = parseFloat(row.precoVendaUSD).toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });

                      }

                    }

                    if (row.quantidade !== undefined && row.quantidade !== "") {
                      totalResult = row.valortotal.toLocaleString('pt-br', { currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      //alert(row.valortotal);
                      atualQtd = row.quantidade.replace(".", ",");
                    } else {
                      totalResult = "";
                      atualQtd = "";
                    }

                    function disableFieldPrice() {
                      return pedidoEmDolar === "S" && row.codctn !== "999" ? true : false;
                    }

                    function disableFieldPriceDolar() {
                      return pedidoEmDolar !== "S" || row.codctn === "999" ? true : false;
                    }

                    function getInfoCorte(id: string) {
                      let tempTable = props.itensTabela;
                      let i = -1;
                      for (let cont = 0; i < tempTable.length; cont++) {
                        const element = tempTable[cont];
                        if (element.codder + element.codpro + element.codctn === id) {
                          i = cont;
                          break;
                        }
                      }
                      if (i >= 0) {
                        if (tempTable[i].quantidadecorte === undefined || tempTable[i].quantidadecorte === "vazio") {
                          return (
                            <Button titleText="Tamanho de corte" classBtn={'btn-cut-add-item'} onClick={() => [handleCorte({ target: { value: 0 } }, i), getFocusCorte(`cut-${row.codder}${row.codpro}${row.codctn}`)]}>
                              <ImScissors />
                            </Button>
                          )
                        } else {
                          return <input type="text" id={`cut-${row.codder}${row.codpro}${row.codctn}`} onChange={(event: any) => handleCorte(event, i)} value={row.quantidadecorte !== 0 ? row.quantidadecorte : ""} style={{ textAlign: 'right', border: '1px solid #ccc', borderRadius: '5px', width: '60%' }} />
                        }
                      }
                    }

                    //Tabela do modal das derivações
                    var tabelDerivacoes = (<TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.codder + row.codpro + row.codctn}
                      selected={isItemSelected}
                      //VERIFICAR SE O MÉTODO DE DEFINIR AS CORES DAS LINHAS POR CLIENTE SERÁ ESSE MESMO.
                      className={props.idItemErro === row.codder + row.codpro + row.codctn ? "additem-linha-error" : isItemSelected ? row.codemp == "2" ? "additem-linha-selecionada" : "additem-linha-selecionada1" : "additem-linha-padrao-" + row.codemp}
                    >
                      {props.allowSelectitem ?
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            inputProps={{ 'aria-labelledby': labelId }}
                            color={'primary'}
                            onClick={(event) => handleClick("checkbox", row.codder + row.codpro + row.codctn)}
                          />
                        </TableCell>
                        :
                        <></>
                      }
                      <TableCell
                        id={labelId}
                        scope="row"
                        padding="none"
                        style={{ paddingLeft: props.allowSelectitem ? "0px" : "1em" }}>
                        {row.codclc}
                      </TableCell>
                      <TableCell align="left">{row.codctn}</TableCell>
                      <TableCell align="left">{row.codemp == "2" ? "GA" : row.codemp == "4" ? "AB" : row.codemp}</TableCell>
                      <TableCell align="left">{row.codder + " - " + row.desder}</TableCell>
                      <TableCell align="left">{row.codpro}</TableCell>
                      <TableCell align="right">{parseFloat(row.salime).toLocaleString('pt-br', { style: 'decimal', maximumFractionDigits: 2, minimumFractionDigits: 2 })}</TableCell>
                      <TableCell align="right">{parseFloat(row.salpro).toLocaleString('pt-br', { style: 'decimal', maximumFractionDigits: 2, minimumFractionDigits: 2 })}</TableCell>
                      <TableCell align="right"><input type="text" onClick={() => handleClickPreco(row.codder + row.codpro + row.codctn)} onChange={(event: any) => handlePreco(event, row.codder + row.codpro + row.codctn)} onBlur={() => onBlurFieldPrice(row.codder + row.codpro + row.codctn)} value={precoUnit} style={{ textAlign: 'right', border: '1px solid #ccc', borderRadius: '5px', width: '60%' }} disabled={disableFieldPrice()} /></TableCell>
                      {pedidoEmDolar === "S" && <TableCell align="right"><input type="text" onClick={() => handleClickPrecoDolar(row.codder + row.codpro + row.codctn)} onChange={(event: any) => handlePrecoDolar(event, row.codder + row.codpro + row.codctn)} onBlur={() => onBlurFieldPrice(row.codder + row.codpro + row.codctn)} value={precoUnitDolar} style={disableFieldPriceDolar() ? { textAlign: 'right', border: '1px solid #ccc', borderRadius: '5px', width: '60%' } : { textAlign: 'right', border: '1px solid #13e4f3', borderRadius: '5px', width: '60%' }} disabled={disableFieldPriceDolar()} /></TableCell>}
                      <TableCell align="right"><input type="text" onChange={(event: any) => handleQuantidade(event, row.codder + row.codpro + row.codctn)} value={atualQtd} style={{ textAlign: 'right', border: '1px solid #ccc', borderRadius: '5px', width: '60%' }} /></TableCell>
                      <TableCell align="right">{getInfoCorte(row.codder + row.codpro + row.codctn)}</TableCell>
                      <TableCell align="right" style={{ whiteSpace: 'nowrap' }}><input type="text" disabled value={totalResult} style={{ textAlign: 'right', border: '1px solid #ccc', borderRadius: '5px', width: '70%' }} /></TableCell>
                    </TableRow>);

                    return (
                      iEstoque == "0" ? tabelDerivacoes : row.codemp == iEstoque ? tabelDerivacoes : null
                    );
                  })}
                {emptyRows > 0 && Math.ceil(props.itensTabela.length / rowsPerPage) > 1 && (
                  <TableRow style={{ height: (dense ? 37 : 57) * emptyRows }} >
                    <TableCell colSpan={10} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {/*
        <PaginationTable
          textSingle="Item"
          textPlural="Itens"
          arrayItens={props.itensTabela}
          page={page}
          countPages={Math.ceil(props.itensTabela.length / rowsPerPage)}
          defaultPage={1}
          handleChange={handleChangePage}
          shape="round"
          styleCLasses={"padding-right"}
          setCurrentPage={setPage}
          setPerPage={setRowsPerPage}
          optionsPerPage={[5, 10, 50]}
          perPage={rowsPerPage}
        />
        */}
        </Paper>
        :
        <></>
      }
    </div >
  );
}